/**
 * Cross-app "you hit a usage cap" signal.
 *
 * The backend denies an over-cap action with HTTP 429 +
 * `{ code: "quota_exceeded", upgrade_url, current, limit, reset_at }`
 * (vernot/.../_shared/usage.ts). Any catch site that has parsed that body can
 * call `maybeDispatchCapHit(body)` to open the single app-wide CapHitModal
 * (mounted by CapHitModalProvider). Mirrors the CustomEvent pattern used by
 * lib/consent.ts so a denial caught anywhere can drive one shared modal.
 */

import { trackPaywallHit } from "@/lib/paywall-analytics";

export const CAP_HIT_EVENT = "cr:cap-hit";

export interface CapHitDetail {
  /** Current usage from the 429 body, when present. */
  current: number | null;
  /** Monthly limit from the 429 body, when present. */
  limit: number | null;
  /** Upgrade destination the backend pointed at (fallback if no next tier). */
  upgradeUrl: string;
  /** ISO reset timestamp from the 429 body, when present. */
  resetAt: string | null;
}

export function dispatchCapHit(detail: CapHitDetail): void {
  if (typeof window === "undefined") return;
  // Deduped in trackPaywallHit: a page-level classifier that already reported
  // this incident with a more specific source wins over this generic one.
  trackPaywallHit("cap_modal", {
    current: detail.current,
    limit: detail.limit,
    upgradeUrl: detail.upgradeUrl,
    resetAt: detail.resetAt,
  });
  window.dispatchEvent(new CustomEvent<CapHitDetail>(CAP_HIT_EVENT, { detail }));
}

/**
 * If `body` is a usage-cap denial body (the canonical
 * { code: "quota_exceeded", upgrade_url, ... }), dispatch the cap-hit event and
 * return true. No-op + false for any other shape. Synchronous — pass the
 * already-parsed Response body (callEdgeFunction / unwrapEdgeError parse it
 * before calling this). The app-wide modal debounces, so a double dispatch from
 * two catch layers is harmless.
 */
export function maybeDispatchCapHit(body: unknown): boolean {
  if (!body || typeof body !== "object") return false;
  const b = body as Record<string, unknown>;
  const isCap = b.code === "quota_exceeded" || typeof b.upgrade_url === "string";
  if (!isCap) return false;
  dispatchCapHit({
    current: typeof b.current === "number" ? b.current : null,
    limit: typeof b.limit === "number" ? b.limit : null,
    upgradeUrl: typeof b.upgrade_url === "string" ? (b.upgrade_url as string) : "/dash/settings/billing",
    resetAt: typeof b.reset_at === "string" ? (b.reset_at as string) : null,
  });
  return true;
}
